import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { QrCode, Plus, Printer, Trash2, MapPin, AlertTriangle, Pencil, Eye, EyeOff, Wifi, ShieldCheck } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const parseIps = (str) => (str || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean);

const isValidIpEntry = (s) => {
  const [addr, mask, extra] = s.split('/');
  if (extra !== undefined) return false;
  if (addr.includes(':')) {
    return /^[0-9a-f:]+$/i.test(addr) && (mask === undefined || (/^\d+$/.test(mask) && +mask <= 128));
  }
  const p = addr.split('.');
  if (p.length !== 4 || !p.every(x => /^\d{1,3}$/.test(x) && +x <= 255)) return false;
  return mask === undefined || (/^\d+$/.test(mask) && +mask <= 32);
};
const invalidIps = (list) => (list || []).filter(s => !isValidIpEntry(s));

const IpLockFields = ({ form, setForm, onDetect, testidPrefix }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <Label className="flex items-center gap-1.5 text-amber-800 mb-0">
        <ShieldCheck className="h-4 w-4" /> Wi-Fi / IP lock (anti-spoofing)
      </Label>
      <Switch
        checked={!!form.enforce_ip}
        onCheckedChange={(v) => setForm(f => ({ ...f, enforce_ip: v }))}
        data-testid={`${testidPrefix}-enforce-ip`}
      />
    </div>
    <p className="text-[11px] text-amber-700 leading-snug">
      When ON, staff can only clock in while on this location's network — defeats GPS spoofing.
      Add the venue's public IP(s) below. Tip: open this page on a device connected to the venue
      Wi-Fi and tap “Detect IP”.
    </p>
    <Textarea
      rows={2}
      value={form.allowed_ips}
      onChange={(e) => setForm(f => ({ ...f, allowed_ips: e.target.value }))}
      placeholder="83.110.21.143  (one per line or comma-separated; CIDR like 83.110.21.0/24 allowed)"
      className="text-xs font-mono"
      data-testid={`${testidPrefix}-allowed-ips`}
    />
    <div className="grid grid-cols-2 gap-2">
      <Button type="button" variant="outline" size="sm" onClick={() => onDetect(false)} className="w-full" data-testid={`${testidPrefix}-detect-ip`}>
        <Wifi className="h-3.5 w-3.5 mr-1.5" /> Detect IP
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => onDetect(true)} className="w-full" data-testid={`${testidPrefix}-detect-subnet`}>
        <Wifi className="h-3.5 w-3.5 mr-1.5" /> Detect + /24 subnet
      </Button>
    </div>
    <p className="text-[10px] text-amber-700 leading-snug">
      Use <b>/24 subnet</b> if the venue's public IP changes its last number from time to time
      (common on business broadband). IPv4 and IPv6 are both supported.
    </p>
    {invalidIps(parseIps(form.allowed_ips)).length > 0 && (
      <p className="text-[11px] text-red-600 flex items-start gap-1" data-testid={`${testidPrefix}-invalid-ips`}>
        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
        <span>Not valid, so they match nobody: <b>{invalidIps(parseIps(form.allowed_ips)).join(', ')}</b>. Use e.g. 217.165.108.25 or 217.165.108.0/24 (IPv4 ranges end in /0–/32).</span>
      </p>
    )}
    {!!form.enforce_ip && parseIps(form.allowed_ips).length === 0 && (
      <p className="text-[11px] text-red-600 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" /> Lock is ON but no IPs added — it won't enforce until you add at least one.
      </p>
    )}
  </div>
);

// Replace each <canvas> inside a container with a PNG <img> so window.print sees it.
const canvasesToImages = (container) => {
  const clone = container.cloneNode(true);
  const origCanvases = container.querySelectorAll('canvas');
  const cloneCanvases = clone.querySelectorAll('canvas');
  origCanvases.forEach((c, i) => {
    try {
      const dataUrl = c.toDataURL('image/png');
      const img = document.createElement('img');
      img.src = dataUrl;
      img.width = c.width;
      img.height = c.height;
      img.style.maxWidth = '100%';
      const target = cloneCanvases[i];
      if (target && target.parentNode) target.parentNode.replaceChild(img, target);
    } catch (_) { /* ignore */ }
  });
  return clone.innerHTML;
};

const StationsTab = ({ branches, reloadBranches }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  // Hide-from-portal is a Super Admin-only control to keep regular admins
  // from accidentally pulling a station out of the employee Clock In/Out picker.
  const userRole = JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const isSuperAdmin = userRole === 'super_admin';

  const [showAddBranch, setShowAddBranch] = useState(false);
  const [showPrintAll, setShowPrintAll] = useState(false);
  const [branchForm, setBranchForm] = useState({ name: '', address: '', latitude: '', longitude: '', radius_m: '250', portal_visible_departments: [], allowed_ips: '', enforce_ip: false });
  const [editBranch, setEditBranch] = useState(null);
  const [qrDialog, setQrDialog] = useState(null);
  const [departments, setDepartments] = useState([]);

  // Load the live department list once for the visibility multi-select.
  // Super Admin-only feature, but the dropdown is harmless for other admins.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/departments`, { headers });
        if (!cancelled) {
          setDepartments((r.data || []).map(d => d.department).filter(Boolean));
        }
      } catch (err) {
        // Non-fatal — picker just stays empty.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addBranch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/clock/branches`, {
        name: branchForm.name,
        address: branchForm.address,
        latitude: branchForm.latitude ? parseFloat(branchForm.latitude) : null,
        longitude: branchForm.longitude ? parseFloat(branchForm.longitude) : null,
        radius_m: branchForm.radius_m ? parseInt(branchForm.radius_m, 10) : 250,
        portal_visible_departments: branchForm.portal_visible_departments || [],
        allowed_ips: parseIps(branchForm.allowed_ips),
        enforce_ip: !!branchForm.enforce_ip,
      }, { headers });
      toast({ title: 'Station added' });
      setShowAddBranch(false);
      setBranchForm({ name: '', address: '', latitude: '', longitude: '', radius_m: '250', portal_visible_departments: [], allowed_ips: '', enforce_ip: false });
      reloadBranches();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const captureMyLocation = (setter) => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS unavailable', description: 'Your browser does not support GPS', variant: 'destructive' });
      return;
    }
    toast({ title: 'Capturing GPS…', description: 'Allow location access if prompted.' });
    navigator.geolocation.getCurrentPosition(
      p => {
        setter(f => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) }));
        toast({ title: 'Location captured', description: `${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}` });
      },
      err => toast({ title: 'GPS failed', description: err.message, variant: 'destructive' }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  };

  // Capture the public IP the server sees from THIS device's current network.
  // Run it on a phone/laptop connected to the venue Wi-Fi to grab that
  // location's IP for the clock-in allowlist.
  const detectMyIp = async (setter, asSubnet = false) => {
    try {
      const r = await axios.get(`${API}/api/clock/my-ip`);
      const ip = (r.data?.ip || '').trim();
      if (!ip) { toast({ title: 'Could not detect IP', variant: 'destructive' }); return; }
      let entry = ip;
      if (asSubnet) {
        if (ip.includes(':')) {
          entry = `${ip.split(':').slice(0, 4).join(':')}::/64`;  // IPv6 → /64
        } else {
          const p = ip.split('.');
          if (p.length === 4) entry = `${p[0]}.${p[1]}.${p[2]}.0/24`;  // IPv4 → /24
        }
      }
      setter(f => {
        const list = parseIps(f.allowed_ips);
        if (list.includes(entry)) {
          toast({ title: 'Already added', description: entry });
          return f;
        }
        list.push(entry);
        return { ...f, allowed_ips: list.join('\n') };
      });
      toast({ title: asSubnet ? 'Subnet detected & added' : 'IP detected & added', description: entry });
    } catch {
      toast({ title: 'Failed to detect IP', variant: 'destructive' });
    }
  };

  const openEditBranch = (b) => {
    setEditBranch({
      id: b.id,
      name: b.name || '',
      address: b.address || '',
      latitude: b.latitude != null ? String(b.latitude) : '',
      longitude: b.longitude != null ? String(b.longitude) : '',
      radius_m: b.radius_m != null ? String(b.radius_m) : '100',
      portal_visible_departments: b.portal_visible_departments || [],
      allowed_ips: (b.allowed_ips || []).join('\n'),
      enforce_ip: !!b.enforce_ip,
    });
  };

  const saveBranch = async () => {
    if (!editBranch) return;
    try {
      await axios.put(`${API}/api/clock/branches/${editBranch.id}`, {
        name: editBranch.name,
        address: editBranch.address,
        latitude: editBranch.latitude ? parseFloat(editBranch.latitude) : null,
        longitude: editBranch.longitude ? parseFloat(editBranch.longitude) : null,
        radius_m: editBranch.radius_m ? parseInt(editBranch.radius_m, 10) : 250,
        portal_visible_departments: editBranch.portal_visible_departments || [],
        allowed_ips: parseIps(editBranch.allowed_ips),
        enforce_ip: !!editBranch.enforce_ip,
      }, { headers });
      toast({ title: 'Station updated' });
      setEditBranch(null);
      reloadBranches();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const deleteBranch = async (id) => {
    if (!window.confirm('Delete this station? The QR will stop working.')) return;
    try {
      await axios.delete(`${API}/api/clock/branches/${id}`, { headers });
      reloadBranches();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const toggleBranch = async (b) => {
    try {
      await axios.put(`${API}/api/clock/branches/${b.id}`, { is_active: !b.is_active }, { headers });
      reloadBranches();
    } catch (err) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const togglePortalVisibility = async (b) => {
    try {
      await axios.put(
        `${API}/api/clock/branches/${b.id}`,
        { hide_from_portal: !b.hide_from_portal },
        { headers }
      );
      toast({
        title: b.hide_from_portal ? 'Visible on Employee Portal' : 'Hidden from Employee Portal',
        description: b.hide_from_portal
          ? `${b.name} now appears in the staff Clock In/Out picker.`
          : `${b.name} will no longer appear in the staff Clock In/Out picker. QR token unchanged.`,
      });
      reloadBranches();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err?.response?.data?.detail || err?.message || 'Please retry.',
        variant: 'destructive',
      });
    }
  };

  const printQR = () => {
    const area = document.getElementById('qr-print-area');
    if (!area) return;
    const html = canvasesToImages(area);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Print QR</title>
      <style>body{font-family:sans-serif;text-align:center;padding:24px;} h1{margin-bottom:4px;} .muted{color:#666;} img{display:block;margin:0 auto;}</style>
      </head><body>${html}<p class="muted" style="margin-top:8px">Scan with your phone camera. Enter Employee Code + PIN to clock in/out.</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const printAllStations = () => {
    const area = document.getElementById('print-all-area');
    if (!area) return;
    const html = canvasesToImages(area);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Clock Stations — All QRs</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body{font-family:sans-serif;margin:0;}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:8mm;page-break-inside:avoid;}
        .tile{border:1px dashed #999;border-radius:8px;padding:10mm 4mm;text-align:center;page-break-inside:avoid;}
        .tile h2{margin:0 0 4px 0;font-size:18pt;}
        .tile .addr{color:#666;font-size:9pt;margin-bottom:8px;}
        .tile img{display:block;margin:0 auto;}
        .tile .steps{font-size:9pt;color:#333;text-align:left;margin:8px auto 0;max-width:60mm;}
        .tile .steps li{margin:2px 0;}
      </style>
      </head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-3" data-testid="stations-tab">
      <div className="flex justify-end gap-2">
        {branches.length > 0 && (
          <Button onClick={() => setShowPrintAll(true)} variant="outline" data-testid="print-all-btn">
            <Printer className="h-4 w-4 mr-1" /> Print All
          </Button>
        )}
        <Button onClick={() => setShowAddBranch(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="add-station-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Station
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.length === 0 ? (
          <Card className="col-span-full border-dashed"><CardContent className="py-10 text-center text-gray-500 text-sm">
            No stations yet. Add one to generate a QR code.
          </CardContent></Card>
        ) : branches.map(b => (
          <Card key={b.id} data-testid={`station-${b.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                    {b.name}
                    {b.is_active ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge> :
                      <Badge className="bg-gray-100 text-gray-600 text-[10px]">Inactive</Badge>}
                    {b.hide_from_portal && (
                      <Badge
                        className="bg-amber-100 text-amber-700 text-[10px] inline-flex items-center gap-0.5"
                        title="Hidden from the employee-portal Clock In/Out picker"
                      >
                        <EyeOff className="h-2.5 w-2.5" /> Portal hidden
                      </Badge>
                    )}
                  </p>
                  {b.address && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{b.address}</p>}
                  {b.portal_visible_departments && b.portal_visible_departments.length > 0 ? (
                    <p
                      className="text-[10px] text-violet-600 mt-1 flex items-center gap-1 flex-wrap"
                      title={`Visible on the employee portal only to: ${b.portal_visible_departments.join(', ')}`}
                    >
                      <span className="font-semibold">Portal scope:</span>
                      {b.portal_visible_departments.map(d => (
                        <span key={d} className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">{d}</span>
                      ))}
                    </p>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-1">Portal scope: All departments</p>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1">
                    {b.latitude != null && b.longitude != null ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-emerald-600">●</span> Geo-fenced ({b.radius_m || 100}m)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-3 w-3" /> No GPS pin set
                      </span>
                    )}
                  </p>
                  {b.enforce_ip && (b.allowed_ips || []).length > 0 && (
                    <p className="text-[11px] mt-1">
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                        <ShieldCheck className="h-3 w-3" /> Wi-Fi lock ON ({(b.allowed_ips || []).length} IP{(b.allowed_ips || []).length > 1 ? 's' : ''})
                      </span>
                    </p>
                  )}
                  {invalidIps(b.allowed_ips).length > 0 && (
                    <p className="text-[11px] mt-1 text-red-600 inline-flex items-center gap-1" data-testid={`station-invalid-ips-${b.id}`}>
                      <AlertTriangle className="h-3 w-3" /> {invalidIps(b.allowed_ips).length} invalid office IP entr{invalidIps(b.allowed_ips).length > 1 ? 'ies' : 'y'} — tap edit to fix
                    </p>
                  )}
                  {b.enforce_ip && (b.allowed_ips || []).length === 0 && (
                    <p className="text-[11px] mt-1">
                      <span className="inline-flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-3 w-3" /> Wi-Fi lock ON but no IPs added
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setQrDialog(b)} className="flex-1" data-testid={`qr-btn-${b.id}`}>
                  <QrCode className="h-4 w-4 mr-1" /> Show QR
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEditBranch(b)} title="Edit station" data-testid={`edit-station-${b.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggleBranch(b)} title="Toggle active">
                  {b.is_active ? 'Disable' : 'Enable'}
                </Button>
                {isSuperAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePortalVisibility(b)}
                    title={b.hide_from_portal ? 'Show on Employee Portal' : 'Hide from Employee Portal'}
                    className={b.hide_from_portal ? 'text-amber-600' : 'text-sky-600'}
                    data-testid={`toggle-portal-${b.id}`}
                  >
                    {b.hide_from_portal ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                    {b.hide_from_portal ? 'Show on Portal' : 'Hide from Portal'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => deleteBranch(b.id)} className="text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Print All Stations dialog */}
      <Dialog open={showPrintAll} onOpenChange={setShowPrintAll}>
        <DialogContent className="max-w-3xl" data-testid="print-all-dialog">
          <DialogHeader><DialogTitle>Print All Stations — A4 ready</DialogTitle></DialogHeader>
          <div id="print-all-area">
            <div className="grid">
              {branches.filter(b => b.is_active).map(b => (
                <div className="tile" key={b.id}>
                  <h2>{b.name}</h2>
                  {b.address && <div className="addr">{b.address}</div>}
                  <QRCodeCanvas
                    value={`${window.location.origin}/clock/${b.qr_token}`}
                    size={180}
                    level="H"
                    includeMargin
                  />
                  <ol className="steps">
                    <li>Scan with your phone camera</li>
                    <li>Enter your <b>Employee Code</b></li>
                    <li>Enter your <b>4-digit PIN</b></li>
                    <li>Tap Clock In / Out</li>
                  </ol>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintAll(false)}>Close</Button>
            <Button onClick={printAllStations} className="bg-violet-600 hover:bg-violet-700" data-testid="print-all-confirm">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Station dialog */}
      <Dialog open={showAddBranch} onOpenChange={setShowAddBranch}>
        <DialogContent data-testid="add-station-dialog">
          <DialogHeader><DialogTitle>Add Clock Station</DialogTitle></DialogHeader>
          <form onSubmit={addBranch} className="space-y-3">
            <div><Label>Name</Label><Input value={branchForm.name} onChange={e => setBranchForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Restaurant, Kitchen, Reception" required data-testid="station-name" /></div>
            <div><Label>Address (optional)</Label><Input value={branchForm.address} onChange={e => setBranchForm(f => ({ ...f, address: e.target.value }))} placeholder="Optional" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Latitude</Label><Input value={branchForm.latitude} onChange={e => setBranchForm(f => ({ ...f, latitude: e.target.value }))} placeholder="25.276987" data-testid="station-lat" /></div>
              <div><Label>Longitude</Label><Input value={branchForm.longitude} onChange={e => setBranchForm(f => ({ ...f, longitude: e.target.value }))} placeholder="55.296249" data-testid="station-lng" /></div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => captureMyLocation(setBranchForm)} className="w-full" data-testid="use-my-location-add">
              <MapPin className="h-4 w-4 mr-1" /> Use my current location
            </Button>
            <div>
              <Label>Geofence radius (meters)</Label>
              <Input
                type="number"
                min="20" max="2000" step="10"
                value={branchForm.radius_m}
                onChange={e => setBranchForm(f => ({ ...f, radius_m: e.target.value }))}
                placeholder="100"
                data-testid="station-radius"
              />
              <p className="text-[10px] text-gray-400 mt-1">Punches outside this radius are allowed but flagged for HR review (the device's GPS accuracy widens this automatically). Without a GPS pin, all punches are allowed but flagged.</p>
            </div>
            {isSuperAdmin && (
              <DepartmentMultiSelect
                value={branchForm.portal_visible_departments}
                onChange={(v) => setBranchForm(f => ({ ...f, portal_visible_departments: v }))}
                departments={departments}
                testIdPrefix="add"
              />
            )}
            <IpLockFields form={branchForm} setForm={setBranchForm} onDetect={(s) => detectMyIp(setBranchForm, s)} testidPrefix="add" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddBranch(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="station-save">Create</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Station dialog */}
      <Dialog open={!!editBranch} onOpenChange={(o) => { if (!o) setEditBranch(null); }}>
        <DialogContent data-testid="edit-station-dialog">
          <DialogHeader><DialogTitle>Edit Clock Station</DialogTitle></DialogHeader>
          {editBranch && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editBranch.name} onChange={e => setEditBranch(f => ({ ...f, name: e.target.value }))} data-testid="edit-station-name" /></div>
              <div><Label>Address</Label><Input value={editBranch.address} onChange={e => setEditBranch(f => ({ ...f, address: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Latitude</Label><Input value={editBranch.latitude} onChange={e => setEditBranch(f => ({ ...f, latitude: e.target.value }))} placeholder="25.276987" data-testid="edit-station-lat" /></div>
                <div><Label>Longitude</Label><Input value={editBranch.longitude} onChange={e => setEditBranch(f => ({ ...f, longitude: e.target.value }))} placeholder="55.296249" data-testid="edit-station-lng" /></div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => captureMyLocation(setEditBranch)} className="w-full" data-testid="use-my-location-edit">
                <MapPin className="h-4 w-4 mr-1" /> Use my current location
              </Button>
              <div>
                <Label>Geofence radius (meters)</Label>
                <Input
                  type="number" min="20" max="2000" step="10"
                  value={editBranch.radius_m}
                  onChange={e => setEditBranch(f => ({ ...f, radius_m: e.target.value }))}
                  data-testid="edit-station-radius"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Tip: 50m = same building · 100m = recommended · 200m = large campus.
                </p>
              </div>
              {editBranch.latitude && editBranch.longitude && (
                <a
                  href={`https://www.google.com/maps?q=${editBranch.latitude},${editBranch.longitude}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs text-violet-600 underline inline-flex items-center gap-1"
                >
                  <MapPin className="h-3 w-3" /> View on Google Maps
                </a>
              )}
              {isSuperAdmin && (
                <DepartmentMultiSelect
                  value={editBranch.portal_visible_departments || []}
                  onChange={(v) => setEditBranch(f => ({ ...f, portal_visible_departments: v }))}
                  departments={departments}
                  testIdPrefix="edit"
                />
              )}
              <IpLockFields form={editBranch} setForm={setEditBranch} onDetect={(s) => detectMyIp(setEditBranch, s)} testidPrefix="edit" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditBranch(null)}>Cancel</Button>
                <Button onClick={saveBranch} className="bg-violet-600 hover:bg-violet-700" data-testid="edit-station-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent data-testid="qr-dialog">
          <DialogHeader><DialogTitle>{qrDialog?.name} — Clock Station QR</DialogTitle></DialogHeader>
          {qrDialog && (
            <div className="space-y-3">
              <div id="qr-print-area" className="flex flex-col items-center p-4 bg-white rounded-lg border">
                <h1 className="text-xl font-bold mb-1">{qrDialog.name}</h1>
                {qrDialog.address && <p className="text-xs text-gray-500 mb-3">{qrDialog.address}</p>}
                <QRCodeCanvas
                  value={`${window.location.origin}/clock/${qrDialog.qr_token}`}
                  size={220}
                  level="H"
                  includeMargin
                />
                <p className="text-[10px] text-gray-400 mt-2 break-all text-center max-w-[220px]">
                  {window.location.origin}/clock/{qrDialog.qr_token}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={printQR} variant="outline" className="flex-1" data-testid="qr-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
                <Button
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/clock/${qrDialog.qr_token}`); toast({ title: 'Link copied' }); }}
                  variant="outline" className="flex-1"
                >Copy Link</Button>
              </div>
              <p className="text-xs text-gray-500 bg-violet-50 rounded p-2">
                💡 Write the same link to an NFC tag — staff can tap instead of scanning.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StationsTab;

/**
 * Multi-select for clock-station portal visibility. Empty selection means
 * "visible to everyone" (the default). Selecting one or more departments
 * scopes the station to those teams only in the employee-portal picker.
 */
const DepartmentMultiSelect = ({ value, onChange, departments, testIdPrefix = 'add' }) => {
  const selected = new Set((value || []).map(v => String(v).toUpperCase()));
  const toggle = (dept) => {
    const up = String(dept).toUpperCase();
    const next = new Set(selected);
    if (next.has(up)) next.delete(up); else next.add(up);
    onChange(Array.from(next));
  };
  const clearAll = () => onChange([]);

  return (
    <div data-testid={`${testIdPrefix}-station-dept-scope`}>
      <Label className="flex items-center justify-between">
        <span>Portal visibility</span>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-[10px] text-violet-600 underline"
            data-testid={`${testIdPrefix}-station-dept-clear`}
          >
            Show to all departments
          </button>
        )}
      </Label>
      <p className="text-[10px] text-gray-400 mt-0.5 mb-1.5">
        Pick departments allowed to see this station in the Employee Portal Clock In/Out picker.
        Leave empty to show it to everyone.
      </p>
      <div className="flex flex-wrap gap-1.5 p-2 border rounded-md max-h-32 overflow-y-auto bg-gray-50">
        {departments.length === 0 ? (
          <span className="text-[11px] text-gray-400">No departments found.</span>
        ) : departments.map((d) => {
          const active = selected.has(d.toUpperCase());
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggle(d)}
              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                active
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-violet-400'
              }`}
              data-testid={`${testIdPrefix}-station-dept-${d.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {d}
            </button>
          );
        })}
      </div>
      {selected.size === 0 && (
        <p className="text-[10px] text-emerald-600 mt-1">All departments will see this station.</p>
      )}
    </div>
  );
};
